import { ME_KEY, queryClient } from './queryClient'

/** Mutating session routes require this custom header (backend `require_csrf`). */
const CSRF_HEADER = 'X-Requested-With'
const CSRF_VALUE = 'XMLHttpRequest'

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** FormData for multipart uploads (no JSON content type). */
  form?: FormData
  signal?: AbortSignal
  /** Skip the global 401 -> logout handling (the login form does its own). */
  allowUnauthorized?: boolean
}

/**
 * Single fetch wrapper for every call in the app: same-origin cookies, the
 * CSRF header on every request, JSON in/out, and a 401 that drops the session
 * so routing bounces to the login page.
 */
export async function request(path: string, opts: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { [CSRF_HEADER]: CSRF_VALUE, Accept: 'application/json' }
  let body: BodyInit | undefined
  if (opts.form) {
    body = opts.form
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(opts.body)
  }
  let res: Response
  try {
    res = await fetch(path, {
      method: opts.method ?? (body ? 'POST' : 'GET'),
      credentials: 'same-origin',
      headers,
      body,
      signal: opts.signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(0, 'Network error — the loco API is unreachable.')
  }
  if (res.status === 401 && !opts.allowUnauthorized) {
    queryClient.setQueryData(ME_KEY, null)
  }
  if (!res.ok) throw new ApiError(res.status, await errorDetail(res))
  return res
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await request(path, opts)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

async function errorDetail(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { detail?: unknown }
    if (typeof data.detail === 'string') return data.detail
    if (data.detail) return JSON.stringify(data.detail)
  } catch {
    /* non-JSON error body */
  }
  return `${res.status} ${res.statusText || 'request failed'}`
}

export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}
